import { formatTime12hFromDb } from './timeDisplay';

const reportDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
});

// Match employee calendar days, not exact timestamps or the viewer's timezone.
const dateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(reportDateFormatter.formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const displayDate = (key) => key ? key.split('-').reverse().join('-') : 'N/A';
const employeeEmail = (record) => String(record.email_address || record.employeeId || record.employee?.email_address || '').trim().toLowerCase();
const employeeInfo = (record) => ({
  employeeEmail: employeeEmail(record),
  employeeName: record.employee ? `${record.employee.first_name} ${record.employee.last_name}` : record.name || 'Unknown',
  employeeCode: record.employee?.empcode || 'N/A',
});
const clockOrNa = (value) => !value || /^00:00(:00)?$/.test(String(value).trim()) ? 'N/A' : formatTime12hFromDb(value);
const visibleWorkMode = value => value === 'WFH' ? 'WFH' : '';
const visibleAttendanceStatus = value => value === 'WO' ? 'Holiday' : value;

const leaveLabel = (leave) => {
  const paid = leave.isPaid ?? (leave.leaveTypeCode === 'PL');
  const fullDay = leave.isFullDay ?? (Number(leave.days) !== 0.5);
  return {
    status: fullDay ? (paid ? 'PL' : 'UPL') : (paid ? 'PHL' : 'UPHL'),
    statusLabel: `${paid ? 'Paid' : 'Unpaid'} ${fullDay ? 'full-day' : 'half-day'} leave`,
  };
};

export const buildReportTableData = (attendanceData, leavesData) => {
  const dailyRows = new Map();
  const requestRows = [];
  const range = attendanceData?.dateRange || leavesData?.dateRange || {};
  const rangeStart = dateKey(range.startDate);
  const rangeEnd = dateKey(range.endDate);

  (attendanceData?.records || []).forEach((record) => {
    const day = dateKey(record.datestring);
    const email = employeeEmail(record);
    const key = email && day ? `${email}:${day}` : `att-${record.id}`;
    dailyRows.set(key, {
      id: `att-${record.id}`, type: 'Attendance', ...employeeInfo(record),
      dateKey: day, date: displayDate(day), status: visibleAttendanceStatus(record.status),
      schedule: record.schedule,
      workMode: visibleWorkMode(record.schedule?.workMode || (record.status === 'WFH' ? 'WFH' : '')),
      inTime: clockOrNa(record.intime), outTime: clockOrNa(record.outtime),
      workTime: record.worktime || 'N/A', breakTime: record.breaktime || '00:00:00',
      overtime: record.overtime || '00:00:00',
      details: `${visibleAttendanceStatus(record.status)} - Work: ${record.worktime || 'N/A'}`,
    });
  });

  (leavesData?.records || []).forEach((record) => {
    const from = dateKey(record.fromDate);
    const to = dateKey(record.toDate) || from;
    const email = employeeEmail(record);
    const details = `${record.leaveTypeCode || 'Leave'} - ${record.days} day(s) - ${record.reason || 'No reason'}`;
    const baseRow = {
      id: `leave-${record.id}`, type: 'Leave', ...employeeInfo(record),
      dateKey: from, date: displayDate(from), status: record.status,
      inTime: 'N/A', outTime: 'N/A', workTime: 'N/A', breakTime: 'N/A', overtime: 'N/A', details,
    };

    // Requests that have not been approved remain separate request entries.
    if (record.status !== 'APPROVED' || !email || !from || !to) {
      requestRows.push(baseRow);
      return;
    }

    const firstDay = [from, rangeStart, record.employmentStart].filter(Boolean).sort().pop();
    const lastDay = [to, rangeEnd, record.employmentEnd].filter(Boolean).sort()[0];
    const label = leaveLabel(record);
    for (let day = firstDay; day <= lastDay;) {
      const key = `${email}:${day}`;
      const existing = dailyRows.get(key);
      const schedule = record.dailySchedules?.[day] || existing?.schedule;
      if (schedule?.managed && !schedule.isWorking) {
        dailyRows.set(key, { ...(existing || baseRow), id: existing?.id || `off-${record.id}-${day}`, type: 'Attendance', dateKey: day, date: displayDate(day), status: visibleAttendanceStatus(schedule.status), schedule, workMode: '', details: schedule.source });
        const next = new Date(`${day}T00:00:00Z`);
        next.setUTCDate(next.getUTCDate() + 1);
        day = next.toISOString().slice(0, 10);
        continue;
      }
      dailyRows.set(key, {
        ...(existing || baseRow),
        id: existing?.id || `leave-${record.id}-${day}`,
        dateKey: day, date: displayDate(day), ...label, leaveStatus: 'APPROVED',
        schedule, workMode: visibleWorkMode(schedule?.workMode || existing?.workMode || ''),
        details: `${label.statusLabel} - APPROVED${record.halfDayPeriod ? ` (${record.halfDayPeriod})` : ''} - ${record.reason || 'No reason'}${existing ? ` - Work: ${existing.workTime}` : ''}`,
      });
      const next = new Date(`${day}T00:00:00Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      day = next.toISOString().slice(0, 10);
    }
  });

  return [...dailyRows.values(), ...requestRows];
};
